import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from './AuthContext'
import { SUPABASE_BACKGROUND_POLLING_DISABLED } from '../supabaseClient.js'

const NotificationContext = createContext()

export const useNotifications = () => {
    return useContext(NotificationContext)
}

export const NotificationProvider = ({ children }) => {
    const { user, profile, organizationId } = useAuth()
    const [notifications, setNotifications] = useState([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(true)

    const fetchNotifications = useCallback(async () => {
        if (SUPABASE_BACKGROUND_POLLING_DISABLED) {
            setNotifications([])
            setUnreadCount(0)
            setLoading(false)
            return
        }

        if (!user) return

        try {
            const today = new Date()
            const fifteenDaysLater = new Date(today)
            fifteenDaysLater.setDate(today.getDate() + 15)

            const todayStr = today.toISOString().split('T')[0]
            const fifteenDaysLaterStr = fifteenDaysLater.toISOString().split('T')[0]

            // 1. Leads Nuevos (solo para usuarios internos; clientes no deben consultar leads)
            const isClient =
                profile?.role === 'client' ||
                profile?.role === 'client_enterprise' ||
                profile?.role === 'cliente'
            const leadsPromise = isClient
                ? Promise.resolve({ data: [] })
                : supabase
                    .from('web_leads')
                    .select('id, nombre, created_at')
                    .eq('status', 'pendiente')
                    .eq('organization_id', organizationId)

            // 2. Renovaciones Próximas (end_date within 15 days, not renewed)
            const renewalsPromise = supabase
                .from('policies')
                .select(`
                    id, 
                    policy_number, 
                    end_date, 
                    status,
                    contacts:client_id (first_name, last_name)
                `)
                .gte('end_date', todayStr)
                .lte('end_date', fifteenDaysLaterStr)
                .neq('status', 'renewed')
                .neq('status', 'cancelled')
                .eq('organization_id', organizationId)

            // 3. Cobranza Pendiente (receipts pending, due_date <= +15 days)
            const collectionsPromise = supabase
                .from('receipts')
                .select(`
                    id, 
                    receipt_number, 
                    due_date, 
                    amount, 
                    policies:policy_id (
                        policy_number, 
                        contacts:client_id (first_name, last_name)
                    )
                `)
                .eq('status', 'pending')
                .lte('due_date', fifteenDaysLaterStr)
            
            const leadsResult = await leadsPromise.catch((reason) => ({ __error: reason }))
            const renewalsResult = await renewalsPromise.catch((reason) => ({ __error: reason }))
            const collectionsResult = await collectionsPromise.catch((reason) => ({ __error: reason }))
            
            let newNotifications = []

            // Process Leads (Centralized)
            if (!leadsResult?.__error && leadsResult?.data) {
                leadsResult.data.forEach((lead) => {
                    newNotifications.push({
                        id: `lead-${lead.id}`,
                        type: 'lead',
                        title: `Nuevo Prospecto: ${lead.nombre}`,
                        message: 'Nuevo prospecto registrado en el sistema.',
                        date: lead.created_at,
                        link: `/commercial/prospects?id=${lead.id}`,
                        priority: 'high'
                    })
                })
            }

            // Process Renewals
            if (!renewalsResult?.__error && renewalsResult?.data) {
                renewalsResult.data.forEach((policy) => {
                    const clientName = policy.contacts
                        ? `${policy.contacts.first_name} ${policy.contacts.last_name || ''}`
                        : 'Cliente'
                    newNotifications.push({
                        id: `renewal-${policy.id}`,
                        type: 'renewal',
                        title: `Póliza por vencer: ${clientName}`,
                        message: `Póliza ${policy.policy_number} vence el ${policy.end_date}`,
                        date: policy.end_date,
                        link: `/polizas/${policy.id}?action=renewal_alert`,
                        priority: 'medium'
                    })
                })
            } else if (renewalsResult?.__error) {
                console.error('Error fetching renewals:', renewalsResult.__error)
            }

            // Process Collections
            if (!collectionsResult?.__error && collectionsResult?.data) {
                collectionsResult.data.forEach((receipt) => {
                    const policy = receipt.policies
                    const clientName = policy?.contacts
                        ? `${policy.contacts.first_name} ${policy.contacts.last_name || ''}`
                        : 'Cliente'
                    newNotifications.push({
                        id: `receipt-${receipt.id}`,
                        type: 'collection',
                        title: `Recordatorio de Pago: ${clientName}`,
                        message: `Recibo de póliza ${policy?.policy_number || ''} vence pronto`,
                        date: receipt.due_date,
                        link: `/cobranza?receipt_id=${receipt.id}&action=payment_reminder`,
                        priority: 'medium'
                    })
                })
            } else if (collectionsResult?.__error) {
                console.error('Error fetching collections:', collectionsResult.__error)
            }

            // Filter read notifications from localStorage
            const readIds = JSON.parse(localStorage.getItem('readNotifications') || '[]')
            
            const processed = newNotifications.map((n) => ({
                ...n,
                read: readIds.includes(n.id)
            }))

            // Sort: High priority first, then by date
            processed.sort((a, b) => {
                // if (a.priority === 'high' && b.priority !== 'high') return -1
                // if (b.priority === 'high' && a.priority !== 'high') return 1
                return new Date(b.date) - new Date(a.date)
            })

            setNotifications(processed)
            setUnreadCount(processed.filter((n) => !n.read).length)
            
        } catch (error) {
            console.error('Error fetching notifications:', error)
        } finally {
            setLoading(false)
        }
    }, [user, profile?.role])

    useEffect(() => {
        fetchNotifications()
        return undefined
    }, [fetchNotifications])

    const markAsRead = (id) => {
        const readIds = JSON.parse(localStorage.getItem('readNotifications') || '[]')
        if (!readIds.includes(id)) {
            const newReadIds = [...readIds, id]
            localStorage.setItem('readNotifications', JSON.stringify(newReadIds))
            
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, read: true } : n))
            )
            setUnreadCount((prev) => Math.max(0, prev - 1))
        }
    }

    const markAllAsRead = () => {
        const allIds = notifications.map((n) => n.id)
        const readIds = JSON.parse(localStorage.getItem('readNotifications') || '[]')
        const newReadIds = [...new Set([...readIds, ...allIds])]
        localStorage.setItem('readNotifications', JSON.stringify(newReadIds))

        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
        setUnreadCount(0)
    }

    const value = {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        loading
    }

    return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}
